/**
 * CommunicationLabelPicker
 *
 * Assigns communication log labels to a log entry.
 * Consumer: `RenderActions`, once per row, for every entry kind — the
 * attachment-rename kind included, which carries no provenance tag but is still
 * a thing a person labels.
 *
 * It holds the ticks made since the last write in `pending`, keyed by value
 * against the `record.labelIds` they came from, because `onSave` runs a
 * transaction and `record` still carries the pre-write assignment when it
 * resolves. `record.labelIds` arriving at any other value supersedes the hold,
 * and a rejected write or a closed popover releases it; nothing times out,
 * since a write that succeeded and was never echoed is showing what the entry
 * holds. Closing the popover also clears `saving`, so a write that never
 * settles cannot leave the boxes dead.
 *
 * Options are iterated over the set's declared order and never sorted; an
 * archived label, or one whose group is archived, is offered only on an entry
 * already carrying it, so an assignment can always be taken off. The control is
 * disabled — never hidden — on a read-only log and where the set defines
 * nothing.
 */

import { FC, useState } from 'react';
import { Button, Checkbox, Popover, Tooltip, Typography } from 'antd';
import { TagsOutlined } from '@ant-design/icons';
import { isEqual } from 'lodash';
import type { Snapshot } from 'valtio';
import type { CommunicationRecord, LabelId, LabelSet } from '#types';
import FlexCol from '@/components/atoms/FlexCol';
import { LabelTags } from '@/components/atoms/LabelTags';
import { labelIsOffered } from '@/components/atoms/labelIsOffered';

const { Text } = Typography;

const EMPTY_LABEL_SET: Snapshot<LabelSet> = { groups: {}, groupOrder: [] };

type PendingAssignment = {
    /** The `record.labelIds` these ticks were derived from; a different value supersedes them. */
    base: readonly LabelId[] | undefined;
    labelIds: LabelId[];
};

interface CommunicationLabelPickerProps {
    record: CommunicationRecord;
    set?: Snapshot<LabelSet>;
    disabled?: boolean;
    /** Resolves false when the write was rejected; the caller has already told
     *  the user, and the record it was given is still what renders. */
    onSave?: (record: CommunicationRecord, labelIds: LabelId[]) => boolean | Promise<boolean>;
}

const CommunicationLabelPicker: FC<CommunicationLabelPickerProps> = ({
    record,
    set = EMPTY_LABEL_SET,
    disabled = false,
    onSave,
}) => {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pending, setPending] = useState<PendingAssignment | null>(null);

    const current = pending && isEqual(pending.base, record.labelIds) ? pending.labelIds : record.labelIds ?? [];
    const assigned = new Set(current);

    const offeredGroups = set.groupOrder
        .map((groupId) => set.groups[groupId])
        .filter((group): group is NonNullable<typeof group> => Boolean(group))
        .map((group) => ({
            group,
            labels: group.labels.filter((label) => labelIsOffered(group, label, assigned)),
        }))
        .filter((offered) => offered.labels.length > 0);

    const disabledReason = !onSave
        ? 'Labels cannot be changed on this log.'
        : disabled
            ? 'This log is read-only, so labels cannot be changed.'
            : offeredGroups.length === 0
                ? 'No communication log labels are defined.'
                : undefined;

    const handleToggle = async (labelId: LabelId, checked: boolean) => {
        if (!onSave) return;
        const nextLabelIds = checked
            ? [...current, labelId]
            : current.filter((id) => id !== labelId);
        setPending({ base: record.labelIds, labelIds: nextLabelIds });
        setSaving(true);
        try {
            const accepted = await onSave(record, nextLabelIds);
            if (!accepted) setPending(null);
        } catch (error) {
            setPending(null);
            throw error;
        } finally {
            setSaving(false);
        }
    };

    const handleOpenChange = (next: boolean) => {
        setOpen(next);
        if (!next) {
            setPending(null);
            setSaving(false);
        }
    };

    const content = (
        <FlexCol gap={10} noGrow>
            {offeredGroups.map(({ group, labels }) => (
                <FlexCol key={group.id} gap={5} noGrow>
                    <Text type="secondary">{group.name}</Text>
                    {labels.map((label) => (
                        <Checkbox
                            key={label.id}
                            checked={assigned.has(label.id)}
                            disabled={saving}
                            onChange={(event) => { void handleToggle(label.id, event.target.checked); }}
                            aria-label={`${label.name} label on communication entry ${record.id}`}
                        >
                            <LabelTags set={set} labelIds={[label.id]} />
                        </Checkbox>
                    ))}
                </FlexCol>
            ))}
        </FlexCol>
    );

    if (disabledReason) {
        return (
            <Tooltip title={disabledReason}>
                <span>
                    <Button
                        type="text"
                        icon={<TagsOutlined />}
                        disabled
                        aria-label={`Labels on communication entry ${record.id}`}
                    />
                </span>
            </Tooltip>
        );
    }

    return (
        <Popover
            content={content}
            title="Labels"
            trigger="click"
            open={open}
            onOpenChange={handleOpenChange}
            placement="bottomRight"
            destroyOnHidden
        >
            <Button
                type="text"
                icon={<TagsOutlined />}
                loading={saving}
                aria-label={`Labels on communication entry ${record.id}`}
            />
        </Popover>
    );
};

export default CommunicationLabelPicker;
